import { Link } from 'react-router-dom';
import { Server, SearchX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { DataTable, Section } from '@/components/audit/CatalogShared';
import {
  campaignHostCounts, hostStatusKey, hostStatusVariant, type MissingCampaignHost,
} from '@/lib/campaigns';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import type { CampaignHostDoc } from '@/types/models';

/**
 * Le suivi machine par machine, alimenté par `onSnapshot` sur
 * `campaigns/{id}/hosts` : chaque ligne avance seule de « attendue » à
 * « prête ». Tant qu'aucune machine n'a utilisé le code, l'état vide explique
 * que c'est normal au lieu d'afficher un tableau désert.
 */
export function CampaignHostsTable({ hosts }: { hosts: CampaignHostDoc[] }) {
  const { t, locale } = useT();
  const counts = campaignHostCounts(hosts);

  if (hosts.length === 0) {
    return (
      <Section title={t('campaign.hosts.title')} description={t('campaign.hosts.desc')}>
        <EmptyState
          icon={<Server className="h-5 w-5" />}
          title={t('campaign.hosts.empty.title')}
          description={t('campaign.hosts.empty.desc')}
        />
      </Section>
    );
  }

  return (
    <Section
      title={t('campaign.hosts.title')}
      description={t('campaign.hosts.desc')}
      aside={
        <span data-testid="campaign-hosts-summary">
          {t('campaign.hosts.summary', {
            ready: counts.ready,
            progress: counts.uploading + counts.processing,
            pending: counts.pending,
            failed: counts.failed,
          })}
        </span>
      }
    >
      <DataTable
        empty={t('audit.catalog.empty')}
        headers={[
          t('campaign.hosts.col.machine'),
          t('campaign.hosts.col.state'),
          t('campaign.hosts.col.audit'),
          t('campaign.hosts.col.updated'),
        ]}
        rows={hosts.map(host => [
          <span data-testid={`campaign-host-${host.slug}`} data-status={host.status}>
            <span className="font-medium text-ink-900 dark:text-white">{host.slug}</span>
            <span className="mt-1 flex flex-wrap gap-1 text-[0.7rem] text-ink-500">
              {host.expected && <span>{t('campaign.hosts.expected')}</span>}
              {host.version > 1 && <span>· {t('campaign.hosts.replaced', { n: host.version - 1 })}</span>}
              {host.lastFailedAuditId && <span>· {t('campaign.hosts.last_failed')}</span>}
            </span>
          </span>,
          <Badge variant={hostStatusVariant(host.status)}>{t(hostStatusKey(host.status))}</Badge>,
          host.auditId
            ? <Link to={`/audits/${host.auditId}`} className="text-brand-600 underline underline-offset-2 dark:text-brand-300">
              {t('campaign.hosts.see_audit')}
            </Link>
            : <span className="text-ink-400">—</span>,
          <span className="text-ink-500">{fmtDate(host.updatedAt, locale)}</span>,
        ])}
      />
    </Section>
  );
}

/**
 * Machines qui manquent encore : annoncées mais jamais reçues, ou vues par le
 * collecteur central sans audit propre. Chaque ligne porte la commande à
 * exécuter — le même code que partout ailleurs dans la campagne.
 */
export function MissingHostsCard({ missing }: { missing: MissingCampaignHost[] }) {
  const { t } = useT();
  return (
    <Section
      title={t('campaign.missing.title')}
      description={t('campaign.missing.desc')}
      aside={<Badge variant={missing.length > 0 ? 'warning' : 'success'}>{missing.length}</Badge>}
    >
      {missing.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <SearchX className="h-4 w-4" /> {t('campaign.missing.none')}
        </div>
      ) : (
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[
            t('campaign.hosts.col.machine'),
            t('campaign.missing.col.origin'),
            t('campaign.missing.col.command'),
          ]}
          rows={missing.map(host => [
            <span data-testid={`campaign-missing-${host.host}`} className="font-medium text-ink-900 dark:text-white">
              {host.host}
            </span>,
            <>
              <Badge variant={host.expected ? 'neutral' : 'info'}>
                {host.expected ? t('campaign.missing.expected') : t('campaign.missing.seen')}
              </Badge>
              <div className="mt-1 text-xs text-ink-500">
                {host.reason === 'not_reported' ? t('campaign.missing.reason.not_reported') : host.hint || host.reason || '—'}
              </div>
              {(host.seenFrom ?? []).length > 0 && (
                <div className="text-xs text-ink-500">{t('campaign.missing.seen_from', { list: host.seenFrom.join(', ') })}</div>
              )}
            </>,
            <code className="block break-all rounded bg-slate-100 px-1.5 py-0.5 text-xs text-ink-800 dark:bg-ink-700 dark:text-ink-100">
              {host.command}
            </code>,
          ])}
        />
      )}
    </Section>
  );
}
